import type { ReactNode } from "react";
import { SAUNA_QUOTE, type SaunaSection } from "../data/sauna-seo";

const EMBER = "hsl(18 82% 52%)";
const EMBER_SOFT = "hsl(22 90% 66%)";
const NAVY = "hsl(222 47% 11%)";

const isExternal = (href: string) => /^https?:\/\//.test(href);
const join = (base: string, href: string) => (isExternal(href) ? href : `${base.replace(/\/+$/, "")}${href}`);
const img = (base: string, file: string) => join(base, `/images/${file}`);
const pad = (n: number) => String(n).padStart(2, "0");
const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(18_82%_52%)] focus-visible:ring-offset-2";

/** Split "Title: body" into parts while keeping every character of the original. */
function splitLead(text: string) {
  const i = text.indexOf(": ");
  if (i > 0 && i < 60) return { lead: text.slice(0, i + 1), rest: text.slice(i + 1) };
  return { lead: "", rest: text };
}

function Arrow() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Check() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Links({ links, base, dark = false }: { links?: SaunaSection["links"]; base: string; dark?: boolean }) {
  if (!links || links.length === 0) return null;
  return (
    <ul className="mt-5 flex flex-wrap gap-2">
      {links.map((l) => {
        const ext = isExternal(l.href);
        const primary = l.href.startsWith(SAUNA_QUOTE.split("?")[0]);
        return (
          <li key={l.href + l.label}>
            <a
              href={join(base, l.href)}
              {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={`group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${focusRing} ${
                primary ? "text-white hover:brightness-110" : dark ? "border border-white/25 text-white hover:bg-white/10" : "border border-border bg-card text-foreground hover:border-[hsl(18_82%_52%)]"
              }`}
              style={primary ? { background: EMBER } : undefined}
            >
              <span>{l.label}</span>
              {ext && <span className="text-xs font-normal opacity-70">(external)</span>}
              <span className="transition-transform group-hover:translate-x-0.5"><Arrow /></span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function Paras({ items, className = "" }: { items: string[]; className?: string }) {
  return <>{items.map((p, i) => <p key={i} className={`leading-relaxed mb-3 last:mb-0 ${className}`}>{p}</p>)}</>;
}

function CheckList({ items, dark = false, cols = false }: { items: string[]; dark?: boolean; cols?: boolean }) {
  return (
    <ul className={`mt-5 grid gap-2.5 ${cols ? "md:grid-cols-2" : ""}`}>
      {items.map((b, i) => (
        <li key={i} className={`flex gap-3 rounded-xl p-3 text-[0.95rem] leading-snug ${dark ? "bg-white/5 text-white/85" : "bg-secondary/60 text-foreground"}`}>
          <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full shrink-0 text-white" style={{ background: EMBER }}><Check /></span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function Eyebrow({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return <p className="font-mono text-xs uppercase tracking-[0.2em] mb-3" style={{ color: dark ? EMBER_SOFT : EMBER }}>{children}</p>;
}

/** Generic compact card, used for any section — guarantees full content. */
function SectionCard({ s, n, base, tone = "card" }: { s: SaunaSection; n: number; base: string; tone?: "card" | "tint" }) {
  return (
    <section className={`h-full rounded-2xl border border-border p-6 md:p-7 ${tone === "tint" ? "bg-secondary/50" : "bg-card"}`}>
      <span className="font-mono text-xs" style={{ color: EMBER }}>{pad(n)}</span>
      <h3 className="font-display font-bold text-xl leading-tight mt-1 mb-3">{s.heading}</h3>
      <div className="text-muted-foreground text-[0.97rem]"><Paras items={s.paragraphs} /></div>
      {s.bullets && <CheckList items={s.bullets} />}
      <Links links={s.links} base={base} />
    </section>
  );
}

export function SaunaLocationContent({ sections, base, image }: { sections: SaunaSection[]; base: string; image: string }) {
  // Structure from data/sauna-locations.ts: planning, focus×F, steps, prepare, note, audiences, commercial×6.
  const total = sections.length;
  const commercialCount = Math.min(6, Math.max(0, total - 6));
  const tailStart = total - commercialCount;
  const coreEnd = tailStart - 4; // steps index
  const valid = coreEnd >= 1;
  if (!valid) {
    return (
      <div id="sauna-location-content" className="scroll-mt-28 container mx-auto px-4 max-w-7xl py-12 grid md:grid-cols-2 gap-4">
        {sections.map((s, i) => <SectionCard key={i} s={s} n={i + 1} base={base} />)}
      </div>
    );
  }
  const planning = sections[0];
  const focus = sections.slice(1, coreEnd);
  const steps = sections[coreEnd];
  const prepare = sections[coreEnd + 1];
  const note = sections[coreEnd + 2];
  const audiences = sections[coreEnd + 3];
  const commercial = sections.slice(tailStart);
  const [cLead, ...cRest] = commercial;
  const cList = cRest.filter((s) => s.bullets && s.bullets.length > 0);
  const cText = cRest.filter((s) => !(s.bullets && s.bullets.length > 0));
  const num = (s: SaunaSection) => sections.indexOf(s) + 1;
  const audienceImgs = ["sauna-detail-2.webp", "sauna-install.webp", "sauna-detail-4.webp", "sauna-detail-5.webp"];

  return (
    <div id="sauna-location-content" className="scroll-mt-28 bg-background">
      {/* 1. Planning + focus bento */}
      <div className="container mx-auto px-4 max-w-7xl py-12 lg:py-16">
        <div className="grid lg:grid-cols-12 gap-4">
          <section className="lg:col-span-7 rounded-3xl p-7 md:p-9 text-white relative overflow-hidden" style={{ background: NAVY }}>
            <div aria-hidden className="absolute -right-24 -top-24 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: EMBER }} />
            <div className="relative">
              <Eyebrow dark>{pad(1)} / Start here</Eyebrow>
              <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight mb-4">{planning.heading}</h2>
              <Paras items={planning.paragraphs} className="text-white/75" />
              {planning.bullets && <CheckList items={planning.bullets} dark />}
              <Links links={planning.links} base={base} dark />
            </div>
          </section>
          <figure className="lg:col-span-5 relative rounded-3xl overflow-hidden min-h-[220px]">
            <img src={img(base, "sauna-heater.webp")} alt="Sauna heater with stones installed in a timber cabin" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_11%/0.7)] to-transparent" />
            <figcaption className="absolute bottom-5 left-6 right-6 text-white font-display font-semibold text-lg">Traditional sauna heating, planned around your space.</figcaption>
          </figure>
        </div>

        {focus.length > 0 && (
          <div className="mt-12">
            <div className="flex items-end justify-between gap-6 mb-5">
              <h2 className="font-display font-bold text-2xl md:text-3xl">What we look at</h2>
              <span className="hidden sm:block font-mono text-xs text-muted-foreground">{focus.length} focus areas</span>
            </div>
            <div className={`grid gap-4 sm:grid-cols-2 ${focus.length % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
              {focus.map((s) => (
                <section key={s.heading} className="group relative rounded-2xl border border-border bg-card p-6 transition-colors hover:border-[hsl(18_82%_52%)]">
                  <span aria-hidden className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full" style={{ background: EMBER }} />
                  <span className="font-mono text-xs text-muted-foreground">{pad(num(s))}</span>
                  <h3 className="font-display font-bold text-lg leading-tight mt-1 mb-2">{s.heading}</h3>
                  <div className="text-muted-foreground text-[0.95rem]"><Paras items={s.paragraphs} /></div>
                  {s.bullets && <CheckList items={s.bullets} />}
                  <Links links={s.links} base={base} />
                </section>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 2. Process timeline */}
      <section className="text-white py-14 lg:py-16" style={{ background: NAVY }}>
        <div className="container mx-auto px-4 max-w-7xl">
          <Eyebrow dark>{pad(num(steps))} / Process</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-3xl mb-8">{steps.heading}</h2>
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-flow-col lg:auto-cols-fr lg:grid-cols-none">
            {steps.paragraphs.map((p, i) => {
              const { lead, rest } = splitLead(p);
              return (
                <li key={i} className="relative rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                  <span className="font-mono text-2xl font-bold" style={{ color: i === 0 ? EMBER : "hsl(0 0% 100% / 0.35)" }}>{pad(i + 1)}</span>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">
                    {lead && <strong className="block font-display text-base text-white mb-1">{lead}</strong>}
                    {rest}
                  </p>
                </li>
              );
            })}
          </ol>
          {steps.bullets && <CheckList items={steps.bullets} dark />}
          <Links links={steps.links} base={base} dark />
        </div>
      </section>

      {/* 3. Prepare + note */}
      <div className="container mx-auto px-4 max-w-7xl py-12 lg:py-16 grid lg:grid-cols-12 gap-4">
        <section className="lg:col-span-7 rounded-3xl border border-border bg-card p-7 md:p-8">
          <Eyebrow>{pad(num(prepare))} / Checklist</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-3xl mb-3">{prepare.heading}</h2>
          <div className="text-muted-foreground"><Paras items={prepare.paragraphs} /></div>
          {prepare.bullets && <CheckList items={prepare.bullets} />}
          <Links links={prepare.links} base={base} />
        </section>
        <section className="lg:col-span-5 rounded-3xl overflow-hidden border border-border bg-secondary/50 flex flex-col">
          <img src={img(base, image)} alt="" aria-hidden="true" loading="lazy" className="w-full aspect-[16/9] object-cover" />
          <div className="p-7">
            <Eyebrow>{pad(num(note))} / Worth knowing</Eyebrow>
            <h2 className="font-display font-bold text-xl md:text-2xl mb-3 leading-tight">{note.heading}</h2>
            <div className="text-muted-foreground"><Paras items={note.paragraphs} /></div>
            {note.bullets && <CheckList items={note.bullets} />}
            <Links links={note.links} base={base} />
          </div>
        </section>
      </div>

      {/* 4. Audiences */}
      <section className="bg-secondary/40 border-y border-border py-12 lg:py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <Eyebrow>{pad(num(audiences))} / Who it's for</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-3xl mb-8 max-w-2xl">{audiences.heading}</h2>
          <div className={`grid gap-4 sm:grid-cols-2 ${audiences.paragraphs.length >= 3 ? "lg:grid-cols-3" : ""}`}>
            {audiences.paragraphs.map((p, i) => {
              const { lead, rest } = splitLead(p);
              return (
                <div key={i} className="rounded-2xl overflow-hidden bg-card border border-border">
                  <img src={img(base, audienceImgs[i % audienceImgs.length])} alt="" aria-hidden="true" loading="lazy" className="w-full aspect-[5/2] object-cover" />
                  <p className="p-5 text-[0.95rem] leading-relaxed text-muted-foreground">
                    {lead && <strong className="block font-display text-lg text-foreground mb-1">{lead}</strong>}
                    {rest}
                  </p>
                </div>
              );
            })}
          </div>
          {audiences.bullets && <CheckList items={audiences.bullets} />}
          <Links links={audiences.links} base={base} />
        </div>
      </section>

      {/* 5. Commercial editorial */}
      {cLead && (
        <div className="container mx-auto px-4 max-w-7xl py-12 lg:py-16">
          <section className="grid lg:grid-cols-12 gap-6 items-center mb-8">
            <div className="lg:col-span-5 rounded-3xl overflow-hidden">
              <img src={img(base, "sauna-panel-assembly.webp")} alt="Sauna wall panels being assembled on site" loading="lazy" className="w-full aspect-[4/3] object-cover" />
            </div>
            <div className="lg:col-span-7">
              <Eyebrow>{pad(num(cLead))} / Commercial</Eyebrow>
              <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight mb-4">{cLead.heading}</h2>
              <div className="text-muted-foreground text-[1.02rem]"><Paras items={cLead.paragraphs} /></div>
              {cLead.bullets && <CheckList items={cLead.bullets} />}
              <Links links={cLead.links} base={base} />
            </div>
          </section>
          {cText.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 mb-4">
              {cText.map((s, i) => (
                <div key={s.heading} className={cText.length % 2 === 1 && i === 0 ? "md:col-span-2" : ""}>
                  <SectionCard s={s} n={num(s)} base={base} tone={i % 2 ? "tint" : "card"} />
                </div>
              ))}
            </div>
          )}
          {cList.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2">
              {cList.map((s) => <SectionCard key={s.heading} s={s} n={num(s)} base={base} tone="tint" />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
